import React, { useState } from 'react';
import { SelectPicker, DateRangePicker } from 'rsuite';
import { ApiPatient, ApiAppointmentCheck } from '../../core/apis';
import { ApiSchemaFilteredAppointmentCheck } from '../../models/api';
import { showToast } from '../../components/toast.component';
import { startOfMonth, endOfMonth, subMonths, startOfWeek, endOfWeek, subWeeks } from 'date-fns';
import { UtilityDate } from '../../utils';
import { VisitListTable } from './visitListTable.component';

// Define DateRange and ValueType locally
type DateRange = [(Date | undefined)?, (Date | undefined)?];
type ValueType = DateRange;

interface VisitListProps {
  companyId: string;
}

const GAP_DAYS = 90; // Updated to 90 days (3 months)

export const VisitList: React.FC<VisitListProps> = ({ companyId }) => {
  const [selectedPatient, setSelectedPatient] = useState<string>("");
  const [appointmentDateRange, setAppointmentDateRange] = useState<ValueType>([undefined, undefined]);
  const [isFetching, setIsFetching] = useState(false);
  const [apiData, setApiData] = useState<ApiSchemaFilteredAppointmentCheck| null>(null);
  const [tableKey, setTableKey] = useState(0); // Add this line
  
  // Fetch patients from the API
  const { data: patients } = ApiPatient.GetAllFromCompany.Hook(companyId);

  // Add this derived state to check if date range is valid
  const hasValidGap = (() => {
    const startDate = appointmentDateRange[0];
    const endDate = appointmentDateRange[1];
    if (!startDate || !endDate) {
      return false;
    }
    return UtilityDate.isValidGap(startDate, endDate, GAP_DAYS);
  })();

  // Transform patient data for SelectPicker
  const patientsSelectItems = patients?.map(patient => ({
    label: `${patient.firstName} ${patient.lastName}`,
    value: patient.sKey
  })) || [];

  const handlePatientChange = (value: string) => {
    setSelectedPatient(value);
    // You can add additional logic here when a patient is selected
  };

  const handleAppointmentDateRangeChange = (value: ValueType) => {
    if (!value || !Array.isArray(value)) {
      setAppointmentDateRange([undefined, undefined]);
      return;
    }
    const [start, end] = value;
    if (start && end) {
      const maxEndDate = new Date(start.getTime());
      maxEndDate.setDate(maxEndDate.getDate() + GAP_DAYS);
      if (end > maxEndDate) {
        setAppointmentDateRange([start, maxEndDate]);
        return;
      }
    }
    setAppointmentDateRange(value);
  };

  const setThisMonth = () => {
    const now = new Date();
    setAppointmentDateRange([startOfMonth(now), endOfMonth(now)]);
  };

  const setLastMonth = () => {
    const now = new Date();
    const lastMonth = subMonths(now, 1);
    setAppointmentDateRange([startOfMonth(lastMonth), endOfMonth(lastMonth)]);
  };

  const setLastTwoMonths = () => {
    const now = new Date();
    const twoMonthsAgo = subMonths(now, 2);
    setAppointmentDateRange([startOfMonth(twoMonthsAgo), endOfMonth(now)]);
  };

  const setLastWeek = () => {
    const now = new Date();
    const lastWeek = subWeeks(now, 1);
    setAppointmentDateRange([
      startOfWeek(lastWeek, { weekStartsOn: 1 }),
      endOfWeek(lastWeek, { weekStartsOn: 1 })
    ]);
  };

  const getDetailedActivity = () => {
    setTableKey(prev => prev + 1); // Add this line
    const startDate = appointmentDateRange[0];
    const endDate = appointmentDateRange[1];

    if (!startDate || !endDate) {
      showToast(
        "Please select appointment date range",
        "info",
        "topEnd",
        5000,
        true
      );
      return;
    }

    if (!hasValidGap) {
      showToast(
        `Start and End Date must be within a ${GAP_DAYS} day range`,
        "info",
        "topEnd",
        15000,
        true
      );
      return;
    }

    const data = {
      pageNumber: 1,
      pageSize: 40,
      nextPageToken: "",
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString(),
      patientId: selectedPatient || undefined,
      companyId
    };

    ApiAppointmentCheck.GetDetailedActivity({
      data,
      onStart: () => setIsFetching(true),
      onEnd: () => setIsFetching(false),
      onSuccess: (response: any) => setApiData(response),
    });
  };

  return (
    <div className="py-1">
      <div className="row g-0 mb-4">
        <div className="col-12 mb-3">
          <div className="d-flex flex-wrap gap-3 p-3 border rounded bg-light">
            <div className="d-flex align-items-center gap-2">
              <span className="fw-bold">Filters:</span>
            </div>
            <div className="d-flex align-items-center gap-2">
              <span>Patient</span>
              <div style={{ minWidth: "200px" }}>
                <SelectPicker
                  data={patientsSelectItems}
                  value={selectedPatient}
                  onChange={value => handlePatientChange(value as string)}
                  searchable
                  cleanable
                  placeholder="Select Patient"
                  block
                />
              </div>
            </div>
            <div className="d-flex align-items-center gap-2">
              <span className="fw-bold">Appointment Date Range:</span>
            </div>
            <div className="d-flex align-items-center gap-2" style={{ minWidth: "300px" }}>
              <DateRangePicker
                value={appointmentDateRange}
                onChange={handleAppointmentDateRangeChange}
                format="MM-dd-yyyy"
                placeholder="Select Date Range"
                style={{ width: "100%" }}
                character=" - "
                disabledDate={(date) => {
                  if (!appointmentDateRange[0]) return false;
                  const maxDate = new Date(appointmentDateRange[0]);
                  maxDate.setDate(maxDate.getDate() + GAP_DAYS);
                  return date > maxDate;
                }}
              />
            </div>
            <div className="d-flex align-items-center gap-2">
              <button className="btn btn-outline-primary btn-sm" onClick={setThisMonth}>This Month</button>
              <button className="btn btn-outline-primary btn-sm" onClick={setLastMonth}>Last Month</button>
              <button className="btn btn-outline-primary btn-sm" onClick={setLastTwoMonths}>Last 2 Months</button>
              <button className="btn btn-outline-primary btn-sm" onClick={setLastWeek}>Last Week</button>
            </div>
            <div className="d-flex gap-2 ms-auto">
              <button 
                className="btn btn-primary btn-sm"
                onClick={getDetailedActivity}
                disabled={isFetching}
              >
                {isFetching ? 'Loading...' : 'Apply Filters'}
              </button>
            </div>
          </div>
        </div>
      </div>
      
      {/* Display data */}
      {apiData && (
        <VisitListTable 
          key={tableKey}
          apiData={apiData.list} 
          isFetching={isFetching}
        />
      )}
    </div>
  );
};